import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { FcmGatewayService } from './gateway/fcm.gateway.service';
import { RegisterFcmTokenRequest, FcmTokenResponse } from '../interfaces/fcm/fcm-interfaces';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { ActionPerformed, PushNotificationSchema, PushNotifications, Token } from '@capacitor/push-notifications';

@Injectable({
  providedIn: 'root',
})
export class FcmService {
  constructor(private fcmGateway: FcmGatewayService, private platform: Platform) {}

  /**
   * Register FCM token for the current device using Capacitor Push Notifications.
   * This method automatically detects the platform, gets the FCM token,
   * and registers it with the backend. No parameters needed.
   * Only works on actual mobile devices (Android/iOS), not on web browsers.
   *
   * @returns Observable of the FCM token response
   * @throws Error when FCM token registration fails or platform is not mobile
   */
  public register(): Observable<FcmTokenResponse> {
    // Check if we're on a mobile device first
    if (!this.isMobileDevice()) {
      return new Observable<FcmTokenResponse>((observer) => {
        observer.error(new Error('FCM token registration is only supported on mobile devices (Android/iOS)'));
      });
    }

    return from(this.registerWithCapacitor()).pipe(
      switchMap(({ deviceType, deviceToken }) => {
        if (!deviceToken) {
          throw new Error('Failed to get FCM token from device');
        }

        const request: RegisterFcmTokenRequest = {
          deviceToken,
          deviceType,
        };

        return this.fcmGateway.registerToken(request);
      }),
      catchError((error) => {
        console.error('Error during FCM token registration:', error);
        // Return an error observable that the caller must handle
        return new Observable<FcmTokenResponse>((observer) => {
          observer.error(new Error(`FCM token registration failed: ${error.message}`));
        });
      })
    );
  }

  /**
   * Register for push notifications using Capacitor and get the FCM token.
   *
   * @returns Promise with device type and FCM token
   * @throws Error when platform is unsupported or FCM token retrieval fails
   */
  private async registerWithCapacitor(): Promise<{ deviceType: 'ANDROID' | 'IOS'; deviceToken: string | null }> {
    try {
      // Detect platform automatically using Capacitor (more reliable than Ionic Platform)
      const platform = Capacitor.getPlatform();
      let deviceType: 'ANDROID' | 'IOS';

      if (platform === 'android') {
        deviceType = 'ANDROID';
      } else if (platform === 'ios') {
        deviceType = 'IOS';
      } else {
        throw new Error('Unsupported platform - only Android and iOS native apps are supported');
      }

      console.log(`Getting push notification token for ${deviceType}...`);

      // Request permission
      const permissionResult = await PushNotifications.requestPermissions();
      if (permissionResult.receive !== 'granted') {
        throw new Error('Push notification permission not granted');
      }

      // Clear any existing listeners first to avoid conflicts
      await PushNotifications.removeAllListeners();

      // Set up listeners BEFORE registering (critical timing)
      return new Promise(async (resolve, reject) => {
        let isResolved = false;

        console.log('Setting up push notification listeners...');

        // Set up success listener
        const registrationListener = await PushNotifications.addListener('registration', (token: Token) => {
          if (!isResolved) {
            isResolved = true;
            console.log(`Push token retrieved for ${deviceType}:`, token.value);
            resolve({ deviceType, deviceToken: token.value });
          }
        });

        // Set up error listener
        const errorListener = await PushNotifications.addListener('registrationError', (error: any) => {
          if (!isResolved) {
            isResolved = true;
            console.error('Push registration error:', error);
            reject(new Error(`Push registration failed: ${JSON.stringify(error)}`));
          }
        });

        // Important: Small delay to ensure listeners are properly set up
        setTimeout(async () => {
          if (!isResolved) {
            try {
              console.log('Calling PushNotifications.register()...');
              await PushNotifications.register();
            } catch (error) {
              if (!isResolved) {
                isResolved = true;
                reject(new Error(`Registration call failed: ${error}`));
              }
            }
          }
        }, 100); // 100ms delay to ensure listeners are ready

        // Timeout after 15 seconds
        setTimeout(() => {
          if (!isResolved) {
            isResolved = true;
            console.warn('Push registration timeout - no token received');
            reject(new Error('Push registration timeout - check Firebase configuration'));
          }
        }, 15000);
      });
    } catch (error) {
      console.error('Error during push notification registration:', error);
      throw error;
    }
  }

  /**
   * Check if the current platform is a mobile device.
   * Excludes web browsers and mobile web browsers.
   * Uses Capacitor.isNativePlatform() to ensure we're on a native app, not a web browser.
   *
   * @returns true if on Android or iOS native app, false otherwise
   */
  private isMobileDevice(): boolean {
    // Use Capacitor to check if we're on a native platform (not web browser)
    // This is more reliable than Ionic Platform which can detect mobile browsers as Android/iOS
    return Capacitor.isNativePlatform();
  }

  /**
   * Add listener for push notifications received when app is open.
   * Call this method to handle incoming notifications.
   */
  public addPushReceivedListener(callback: (notification: PushNotificationSchema) => void): void {
    PushNotifications.addListener('pushNotificationReceived', callback);
  }

  /**
   * Add listener for push notification actions performed.
   * Call this method to handle when user taps on notifications.
   */
  public addPushActionPerformedListener(callback: (notification: ActionPerformed) => void): void {
    PushNotifications.addListener('pushNotificationActionPerformed', callback);
  }

  /**
   * Remove all push notification listeners.
   * Call this when cleaning up the service.
   */
  public removeAllListeners(): void {
    PushNotifications.removeAllListeners();
  }
}
